<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import Flashcard from "@edu-quizz/shared/components/flashcard/Flashcard.svelte";
    import FlashcardEmptyState from "@edu-quizz/shared/components/flashcard/FlashcardEmptyState.svelte";
    import FlashcardResults from "@edu-quizz/shared/components/flashcard/FlashcardResults.svelte";
    import ProgressBar from "@edu-quizz/shared/components/common/ProgressBar.svelte";
    import NavigationButtons from "@edu-quizz/shared/components/common/NavigationButtons.svelte";
    import DifficultyButtons from "@edu-quizz/shared/components/common/DifficultyButtons.svelte";
    import BackButton from "@edu-quizz/shared/components/common/BackButton.svelte";
    import { loadDeckSpaced, saveReviewSpaced, resetDeckReviews, getSpacedReviewStats } from "../lib/flashcardLogic.js";

    export let params = {};

    let deck = null;
    let cards = [];
    let loading = true;
    let error = null;

    // Flashcard state
    let currentCardIndex = 0;
    let isFlipped = false;
    let showResults = false;
    let reviewedCards = [];

    // Spaced review stats
    let spacedStats = { remainingCards: 0, nextUnlockDate: null };

    $: currentCard = cards[currentCardIndex];
    $: currentCardIsReversed = currentCard?.isReversed || false;
    $: hasNext = currentCardIndex < cards.length - 1;
    $: hasPrev = currentCardIndex > 0;

    console.log("currentCard", currentCard);

    async function loadDeck() {
        loading = true;
        error = null;

        try {
            const deckId = parseInt(params.id);
            const result = await loadDeckSpaced(deckId);

            deck = result.deck;
            cards = result.cards;
            reviewedCards = new Array(cards.length).fill(null);

            // Get spaced review stats (remaining cards and next unlock date)
            if (cards.length === 0) {
                spacedStats = await getSpacedReviewStats(deckId);
            }

            loading = false;
        } catch (err) {
            console.error("Error loading deck:", err);
            error = err.message;
            loading = false;
        }
    }

    function flipCard() {
        isFlipped = !isFlipped;
    }

    function nextCard() {
        if (hasNext) {
            currentCardIndex++;
            isFlipped = false;
        } else if (
            currentCardIndex === cards.length - 1 &&
            reviewedCards.every((r) => r !== null)
        ) {
            // All cards reviewed, show results
            showResults = true;
        }
    }

    function prevCard() {
        if (hasPrev) {
            currentCardIndex--;
            isFlipped = false;
        }
    }

    async function markDifficulty(level) {
        // Prevent marking the same card multiple times
        if (reviewedCards[currentCardIndex] !== null) {
            console.log("exit ...");
            return;
        }

        reviewedCards[currentCardIndex] = level;

        // Save the review to the database
        try {
            console.log("try saving");
            await saveReviewSpaced(deck, currentCard, level);
        } catch (err) {
            console.error("Error saving flashcard review:", err);
        }

        nextCard();
    }

    function restart() {
        currentCardIndex = 0;
        isFlipped = false;
        showResults = false;
        reviewedCards = new Array(cards.length).fill(null);
    }

    async function handleResetDeck() {
        if (
            !confirm(
                "Voulez-vous vraiment réinitialiser toutes les révisions de ce deck ? Vous pourrez réviser toutes les cartes à nouveau.",
            )
        ) {
            return;
        }

        try {
            const deckId = parseInt(params.id);
            await resetDeckReviews(deckId);

            // Return to mode selection
            push(`/flashcard/${deckId}`);
        } catch (err) {
            console.error("Error resetting deck reviews:", err);
            error = "Erreur lors de la réinitialisation des révisions";
        }
    }

    function goBack() {
        push("/");
    }

    function handleKeyPress(event) {
        if (showResults) return;

        if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            if (!isFlipped) {
                flipCard();
            }
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            if (isFlipped) {
                nextCard();
            }
        } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            prevCard();
        } else if (isFlipped && event.key >= "1" && event.key <= "3") {
            event.preventDefault();
            markDifficulty(parseInt(event.key));
        }
    }

    onMount(() => {
        loadDeck();
        window.addEventListener("keydown", handleKeyPress);
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    });
</script>

<div class="flashcard-page">
    <header class="page-header">
        <BackButton onClick={goBack} />
        {#if deck}
            <div class="header-content">
                <h1 class="text-base-content">{deck.name}</h1>
                <span class="mode-badge bg-primary text-primary-content">Mode espacé</span>
            </div>
        {/if}
    </header>

    {#if loading}
        <div class="loading">
            <div class="spinner loading-spinner loading-lg border-primary border-t-transparent"></div>
            <p>Chargement du deck...</p>
        </div>
    {:else if error}
        <div class="error text-base-content/70">
            <p>Erreur: {error}</p>
            <button class="btn btn-primary" on:click={loadDeck}>Réessayer</button>
        </div>
    {:else if cards.length === 0}
        <FlashcardEmptyState
            onReset={handleResetDeck}
            onBack={goBack}
            remainingCards={spacedStats.remainingCards}
            nextUnlockDate={spacedStats.nextUnlockDate}
        />
    {:else if showResults}
        <FlashcardResults
            totalCards={cards.length}
            {reviewedCards}
            onBack={goBack}
            onReset={handleResetDeck}
        />
    {:else}
        <div class="deck-container">
            {#if deck.description}
                <p class="deck-description bg-base-200">{deck.description}</p>
            {/if}

            <ProgressBar
                current={currentCardIndex + 1}
                total={cards.length}
                label="Carte {currentCardIndex + 1} sur {cards.length}"
            />

            <Flashcard
                {isFlipped}
                frontContent={currentCardIsReversed ? currentCard.back : currentCard.front}
                backContent={currentCardIsReversed ? currentCard.front : currentCard.back}
                frontImages={currentCardIsReversed ? currentCard.answerImages : currentCard.questionImages}
                backImages={currentCardIsReversed ? currentCard.questionImages : currentCard.answerImages}
                frontAudio={currentCardIsReversed ? null : currentCard.questionAudio}
                onFlip={flipCard}
            />

            {#if isFlipped}
                <DifficultyButtons
                    onMarkDifficulty={markDifficulty}
                    isReviewed={reviewedCards[currentCardIndex] !== null}
                />
            {:else}
                <NavigationButtons
                    onPrev={prevCard}
                    onNext={nextCard}
                    {hasPrev}
                    {hasNext}
                />
            {/if}

            <div class="keyboard-hints text-base-content/50">
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">Espace</kbd> Retourner la carte</span>
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">←</kbd> Précédent</span>
                <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">→</kbd> Suivant</span>
                {#if isFlipped}
                    <span><kbd class="kbd kbd-sm bg-base-200 border-base-300">1-3</kbd> Difficulté</span>
                {/if}
            </div>
        </div>
    {/if}
</div>

<style>
    .flashcard-page {
        min-height: 100vh;
        padding: 1.5rem;
        max-width: 800px;
        margin: 0 auto;
    }

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    .header-content {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
    }

    h1 {
        margin: 0;
        font-size: 2rem;
    }

    .mode-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.875rem;
        font-weight: 600;
    }

    .loading,
    .error {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 1rem;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .error button {
        border: none;
        padding: 0.6rem 1.2rem;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.95rem;
    }

    .deck-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .deck-description {
        font-size: 1rem;
        line-height: 1.6;
        margin: 0;
        padding: 1rem;
        border-radius: 8px;
    }

    .keyboard-hints {
        display: flex;
        gap: 1rem;
        justify-content: center;
        flex-wrap: wrap;
        font-size: 0.85rem;
    }

    kbd {
        padding: 0.2rem 0.5rem;
        border-radius: 4px;
        border: 1px solid;
        font-family: monospace;
        font-size: 0.8rem;
    }

    @media (max-width: 640px) {
        .flashcard-page {
            padding: 1rem;
        }

        h1 {
            font-size: 1.5rem;
        }

        .keyboard-hints {
            display: none;
        }
    }
</style>
